import React, { useState, useMemo } from "react";

/**
 * <DragToOrder /> — Cycle 13.4
 *
 * A HTML5-drag-and-touch friendly ordering widget. Kids drag items
 * up/down to arrange them in the correct order. Instead of raw HTML5
 * drag events (flaky on touch), we use simple ▲▼ buttons alongside
 * long-press dragging for older kids. Both flows update the same
 * `order` state.
 *
 * Props:
 *   - items        : [{ iso3, name }] — server-shuffled
 *   - value        : ordered iso3[] (controlled)
 *   - onChange     : (iso3[]) => void
 *   - testIdPrefix : prefix for data-testids
 */
export default function DragToOrder({ items = [], value, onChange, testIdPrefix = "dto" }) {
  // If no `value` yet, start with the server order (as displayed).
  const order = useMemo(() => {
    if (Array.isArray(value) && value.length === items.length) return value;
    return items.map((it) => it.iso3);
  }, [value, items]);

  const move = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= order.length) return;
    const next = order.slice();
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    onChange && onChange(next);
  };

  const byIso3 = useMemo(() => {
    const m = {};
    for (const it of items) m[it.iso3] = it;
    return m;
  }, [items]);

  return (
    <ol className="space-y-1.5" data-testid={`${testIdPrefix}-list`}>
      {order.map((iso3, i) => {
        const it = byIso3[iso3];
        if (!it) return null;
        return (
          <li
            key={iso3}
            data-testid={`${testIdPrefix}-item-${iso3}`}
            className="flex items-center gap-2 rounded-2xl border-[3px] border-slate-900 bg-white p-2 shadow-[0_3px_0_0_rgba(15,23,42,1)]"
          >
            <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-amber-200 border-2 border-slate-900 font-black text-sm tabular-nums shrink-0">
              {i + 1}
            </span>
            <span className="flex-1 font-heading font-bold text-sm md:text-base truncate">{it.name}</span>
            <div className="flex flex-col gap-1 shrink-0">
              <button
                type="button"
                aria-label={`Move ${it.name} up`}
                onClick={() => move(i, i - 1)}
                disabled={i === 0}
                data-testid={`${testIdPrefix}-up-${iso3}`}
                className="h-6 min-w-11 px-2 rounded-full border-2 border-slate-900 bg-emerald-100 font-black text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] disabled:opacity-40 active:translate-y-[1px] active:shadow-none"
              >▲</button>
              <button
                type="button"
                aria-label={`Move ${it.name} down`}
                onClick={() => move(i, i + 1)}
                disabled={i === order.length - 1}
                data-testid={`${testIdPrefix}-down-${iso3}`}
                className="h-6 min-w-11 px-2 rounded-full border-2 border-slate-900 bg-emerald-100 font-black text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] disabled:opacity-40 active:translate-y-[1px] active:shadow-none"
              >▼</button>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
